import { useState, useRef, useEffect, useCallback } from 'react';
import { TelemetryPanelConfig, TelemetryLayoutMode } from '../types/telemetry-dashboard.types';
import {
  TELEMETRY_GRID_COLS,
  TELEMETRY_GRID_ROW_H,
  TELEMETRY_GRID_GAP,
  TELEMETRY_GRID_PAD,
  snapTelemetryPanelToGrid,
  moveTo,
  resizeRect,
} from '../utils/telemetry-grid.utils';

export function useTelemetryLayout(
  layout: TelemetryPanelConfig[],
  setLayout: (layout: TelemetryPanelConfig[]) => void,
  layoutMode: TelemetryLayoutMode = 'grid'
) {
  const [gridW, setGridW] = useState(960);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragPos, setDragPos] = useState<{ top: number; left: number } | null>(null);

  const [resizeId, setResizeId] = useState<string | null>(null);
  const [resizePos, setResizePos] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    initX: number;
    initY: number;
    curX: number;
    curY: number;
  } | null>(null);

  const resizeRef = useRef<{
    id: string;
    dir: string;
    startX: number;
    startY: number;
    initX: number;
    initY: number;
    initW: number;
    initH: number;
    curX: number;
    curY: number;
    curW: number;
    curH: number;
  } | null>(null);

  const gridScrollRef = useRef<HTMLDivElement>(null);
  const gridElRef = useRef<HTMLDivElement>(null);

  // Column width calculation matching container width
  const colW = Math.max(40, (gridW - TELEMETRY_GRID_GAP * (TELEMETRY_GRID_COLS - 1)) / TELEMETRY_GRID_COLS);

  const pxX = useCallback((x: number) => x * (colW + TELEMETRY_GRID_GAP), [colW]);
  const pxY = useCallback((y: number) => y * (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP), []);
  const pxW = useCallback((w: number) => w * colW + (w - 1) * TELEMETRY_GRID_GAP, [colW]);
  const pxH = useCallback((h: number) => h * TELEMETRY_GRID_ROW_H + (h - 1) * TELEMETRY_GRID_GAP, []);

  // Measure container width via ResizeObserver
  const measure = useCallback(() => {
    const el = gridScrollRef.current || gridElRef.current;
    if (!el) return;
    const w = el.clientWidth - TELEMETRY_GRID_PAD * 2;
    if (w > 0) {
      setGridW((prev) => (Math.abs(w - prev) > 1 ? w : prev));
    }
  }, []);

  useEffect(() => {
    const el = gridScrollRef.current || gridElRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    measure();

    const t1 = setTimeout(measure, 50);
    const t2 = setTimeout(measure, 150);
    const t3 = setTimeout(measure, 300);

    return () => {
      ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [measure]);

  // Handle Drag Pointer Down (Enabled in grid and list modes)
  const onDragDown = useCallback(
    (id: string, e: React.PointerEvent) => {
      if (layoutMode !== 'grid' && layoutMode !== 'list') return;
      if ((e.target as HTMLElement).closest('[data-nodrag]')) return;
      const panel = layoutRef.current.find((p) => p.id === id);
      if (!panel) return;
      e.stopPropagation();

      dragRef.current = {
        id,
        startX: e.clientX,
        startY: e.clientY,
        initX: pxX(panel.x),
        initY: pxY(panel.y),
        curX: pxX(panel.x),
        curY: pxY(panel.y),
      };
      setDragId(id);
      setDragPos({ top: pxY(panel.y), left: pxX(panel.x) });
    },
    [layoutMode, pxX, pxY]
  );

  // Handle Resize Pointer Down (Supported in grid and list modes)
  const onResizeDown = useCallback(
    (id: string, dir: string, e: React.PointerEvent) => {
      e.stopPropagation();
      const panel = layoutRef.current.find((p) => p.id === id);
      if (!panel) return;

      resizeRef.current = {
        id,
        dir,
        startX: e.clientX,
        startY: e.clientY,
        initX: pxX(panel.x),
        initY: pxY(panel.y),
        initW: pxW(panel.w),
        initH: pxH(panel.h),
        curX: pxX(panel.x),
        curY: pxY(panel.y),
        curW: pxW(panel.w),
        curH: pxH(panel.h),
      };
      setResizeId(id);
      setResizePos({
        left: pxX(panel.x),
        top: pxY(panel.y),
        width: pxW(panel.w),
        height: pxH(panel.h),
      });
    },
    [pxX, pxY, pxW, pxH]
  );

  // Synchronous PointerMove and PointerUp handlers
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        const nextLeft = dragRef.current.initX + dx;
        const nextTop = dragRef.current.initY + dy;
        dragRef.current.curX = nextLeft;
        dragRef.current.curY = nextTop;
        setDragPos({ left: nextLeft, top: nextTop });

        if (layoutMode === 'grid') {
          const gx = Math.round(nextLeft / (colW + TELEMETRY_GRID_GAP));
          const gy = Math.round(nextTop / (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP));
          const cur = layoutRef.current.find((p) => p.id === dragRef.current?.id);
          if (cur && (gx !== cur.x || gy !== cur.y)) {
            setLayout(moveTo(layoutRef.current, dragRef.current.id, gx, gy));
          }
        }
      } else if (resizeRef.current) {
        const { dir, startX, startY, initX, initY, initW, initH } = resizeRef.current;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        let newW = initW;
        let newH = initH;
        let newX = initX;
        let newY = initY;

        if (dir.includes('e')) newW = Math.max(pxW(2), initW + dx);
        if (dir.includes('s')) newH = Math.max(pxH(2), initH + dy);
        if (dir.includes('w')) {
          newW = Math.max(pxW(2), initW - dx);
          newX = initX + (initW - newW);
        }
        if (dir.includes('n')) {
          newH = Math.max(pxH(2), initH - dy);
          newY = initY + (initH - newH);
        }

        resizeRef.current.curX = newX;
        resizeRef.current.curY = newY;
        resizeRef.current.curW = newW;
        resizeRef.current.curH = newH;
        setResizePos({ left: newX, top: newY, width: newW, height: newH });
      }
    };

    const onPointerUp = () => {
      if (dragRef.current) {
        const { id, startY, curY } = dragRef.current;
        const dy = curY - dragRef.current.initY;
        if (layoutMode === 'list') {
          const idx = layoutRef.current.findIndex((p) => p.id === id);
          if (idx >= 0 && Math.abs(dy) > 30) {
            const shift = Math.round(dy / 200);
            const targetIdx = Math.max(0, Math.min(layoutRef.current.length - 1, idx + shift));
            if (targetIdx !== idx) {
              const copy = [...layoutRef.current];
              const [moved] = copy.splice(idx, 1);
              copy.splice(targetIdx, 0, moved);
              setLayout(copy);
            }
          }
        } else {
          const gx = Math.round(dragRef.current.curX / (colW + TELEMETRY_GRID_GAP));
          const gy = Math.round(curY / (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP));
          setLayout(moveTo(layoutRef.current, id, gx, gy));
        }
      } else if (resizeRef.current) {
        const { id, curW, curH, curX, curY, dir } = resizeRef.current;
        const panel = layoutRef.current.find((p) => p.id === id);
        if (panel) {
          const nw = Math.max(2, Math.round((curW + TELEMETRY_GRID_GAP) / (colW + TELEMETRY_GRID_GAP)));
          const nh = Math.max(2, Math.round((curH + TELEMETRY_GRID_GAP) / (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP)));
          const nx = dir.includes('w') ? Math.max(0, Math.round(curX / (colW + TELEMETRY_GRID_GAP))) : panel.x;
          const ny = dir.includes('n') ? Math.max(0, Math.round(curY / (TELEMETRY_GRID_ROW_H + TELEMETRY_GRID_GAP))) : panel.y;
          setLayout(resizeRect(layoutRef.current, id, nx, ny, nw, nh));
        }
      }
      dragRef.current = null;
      resizeRef.current = null;
      setDragId(null);
      setDragPos(null);
      setResizeId(null);
      setResizePos(null);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [colW, pxW, pxH, setLayout]);

  return {
    colW,
    pxX,
    pxY,
    pxW,
    pxH,
    dragId,
    dragPos,
    resizeId,
    resizePos,
    gridScrollRef,
    gridElRef,
    onDragDown,
    onResizeDown,
    layoutMode
  };
}
